import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  CheckFilledIcon,
  ExclamationOutlineIcon,
  ExitIcon,
  InfoIcon,
  RemoveFilledIcon,
} from '@icons';
import { cn } from '@shared/util/classnames';
import { Button, ButtonProps } from '@ui/Button';

const DEFAULT_TOAST_DURATION = 10000;

export type ToastState = 'animate-toast-in' | 'animate-toast-out';

export type ToastStyle = 'confirmation' | 'warning' | 'info' | 'important';

export interface ToastProps {
  style?: ToastStyle;
  position?: 'top' | 'bottom';
  message?: string | ReactNode;
  icon?: ReactNode;
  className?: string;
  duration?: number;
  autoClose?: boolean;
  tapToClose?: boolean;
  prioritize?: boolean;
  onClose?: () => void;
  actions?: ToastActionProps[];
  showCloseButton?: boolean;
}

export function Toast({
  style = 'confirmation',
  icon = getToastIcon({ style }),
  message,
  className,
  duration = DEFAULT_TOAST_DURATION,
  autoClose = true,
  onClose,
  actions = [],
  showCloseButton = actions.length > 0,
  tapToClose = actions.length === 0 || !showCloseButton,
}: ToastProps) {
  const [mountTime] = useState(Date.now());
  const [state, setState] = useState<ToastState>('animate-toast-in');
  const ref = useRef<HTMLDivElement>(null);

  // if autoClose is set, close the toast after the duration
  useEffect(() => {
    let timer = 0;
    // if mounted for longer than the duration, close immediately
    const timeElapsed = Date.now() - mountTime;
    const delay = duration - timeElapsed;
    if (autoClose && state === 'animate-toast-in') {
      timer = window.setTimeout(
        () => {
          setState('animate-toast-out');
        },
        Math.max(delay, 200),
      );
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [autoClose, duration, mountTime, state]);

  // if the toast is animating out, call onClose when the animation ends
  useEffect(() => {
    const toast = ref.current;
    if (toast && onClose && state === 'animate-toast-out') {
      toast.addEventListener('animationend', onClose, { once: true });
    }
    return () => {
      if (toast && onClose) {
        toast.removeEventListener('animationend', onClose);
      }
    };
  }, [onClose, state]);

  function closeToast() {
    setState('animate-toast-out');
  }

  return (
    <div className={state} ref={ref}>
      <div className="px-4 pb-4">
        <div
          role={tapToClose ? 'button' : undefined}
          onClick={tapToClose ? closeToast : undefined}
          className={cn(
            'pointer-events-auto',
            'flex flex-row items-center gap-2 w-full',
            'border-1 border-black/10 shadow-md rounded-lg',
            'text-md font-medium leading-tight',
            className,
            { 'pl-2 pr-6 py-2': icon },
            { 'px-6 py-4': !icon },
            { 'bg-coast-green-10': style === 'confirmation' },
            { 'bg-tropical-red-40': style === 'warning' },
            { 'bg-white': style === 'info' },
            { 'bg-foreground text-background': style === 'important' },
            { 'cursor-pointer': tapToClose },
          )}
        >
          {icon && <div className="shrink-0 w-10 h-10 [&>svg]:w-full [&>svg]:h-auto">{icon}</div>}
          <div className="leading-tight line-clamp-3 grow py-1">{message}</div>
          {actions.map((action, index) => (
            <ToastAction
              key={index}
              {...action}
              onPress={() => {
                action.onPress?.();
                closeToast();
              }}
            />
          ))}
          {showCloseButton && <CloseToastButton onPress={closeToast} />}
        </div>
      </div>
    </div>
  );
}

export interface ToastActionProps {
  onPress?: () => void;
  href?: string;
  label: string;
}

function ToastAction({ onPress, href, label }: ToastActionProps) {
  return (
    <Button
      color="transparent"
      onPress={onPress}
      href={href}
      className="uppercase px-0 min-w-0 shrink-0"
    >
      {label}
    </Button>
  );
}

function CloseToastButton({ onPress }: ButtonProps) {
  return (
    <Button color="transparent" isIconOnly onPress={onPress} className="-mr-4 ml-1">
      <ExitIcon className="w-6 h-6" strokeWidth={3} />
    </Button>
  );
}

function getToastIcon({ style }: ToastProps) {
  switch (style) {
    case 'confirmation':
      return <CheckFilledIcon />;
    case 'warning':
      return <RemoveFilledIcon />;
    case 'info':
      return <InfoIcon />;
    case 'important':
      return <ExclamationOutlineIcon />;
    default:
      return null;
  }
}
